import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import Footer from "./Footer";
import Nav from "./Nav";
import { DCV2_CSS, FONT_HREF } from "./styles";
import {
  paletteVars,
  readStoredTheme,
  themeVals,
  writeStoredTheme,
  type ThemeName,
  type ThemeVals,
} from "./theme";
import { useDcMotion } from "./useDcMotion";

/**
 * The page shell shared by the homepage and the About Us destinations.
 *
 * Everything that makes a page look like the v2 canvas lives here rather than
 * in each route: the two fonts, the stylesheet, the eleven custom properties,
 * the persisted theme, the nav, the footer and the scroll runtime. A route that
 * wraps its content in this gets the whole design language and only has to
 * supply its own sections.
 *
 * It exists because the alternative — copying the root element and its var
 * block into five routes — would guarantee they drift the first time a token
 * changes. The canvas has one root; so does this.
 *
 * Pages read the active theme through `useDcTheme` rather than props, because
 * the values are needed several levels down (the hero's video and starfield
 * cross-fade on it) and threading them through every intermediate section would
 * couple layout components to the theme for no reason.
 */

interface DcThemeContextValue {
  theme: ThemeName;
  dark: boolean;
  vals: ThemeVals;
  toggleTheme: () => void;
}

const DcThemeContext = createContext<DcThemeContextValue | null>(null);

export function useDcTheme(): DcThemeContextValue {
  const ctx = useContext(DcThemeContext);
  if (!ctx) {
    throw new Error("useDcTheme must be used inside <DcPage>");
  }
  return ctx;
}

const FONT_LINK_ID = "dcv2-fonts";
const STYLE_ID = "dcv2-styles";

export interface DcPageProps {
  children: ReactNode;
  /**
   * Theme for a visitor with no stored preference.
   *
   * Dark, where the canvas defaulted to light. A returning visitor's own choice
   * still wins — this only decides the first impression.
   */
  defaultTheme?: ThemeName;
  /** 0-2. The canvas ships 2. */
  parallaxIntensity?: number;
  /**
   * Drop the footer.
   *
   * For the auth surfaces only. A visitor on a sign-in page arrived with one
   * intention, and every link in a sitemap under the form is a way to not
   * finish it; the way out they actually need is the nav above and the
   * "Back to home" inside the form. The page is already about 930px tall, so
   * the footer is reached only by scrolling past the thing they came for.
   */
  hideFooter?: boolean;
}

export default function DcPage({
  children,
  defaultTheme = "dark",
  parallaxIntensity = 2,
  hideFooter = false,
}: DcPageProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  /**
   * Resolve the stored theme DURING the first render, not after it.
   *
   * This used to initialise to `defaultTheme` and read localStorage in a mount
   * effect. Every route renders its own <DcPage>, so navigating unmounted one
   * and mounted another: each navigation painted a full dark frame and then
   * corrected to the visitor's stored light theme. A flash on every page
   * change, for anyone not using the default.
   *
   * The original reasoning was that reading storage in an initialiser makes
   * the first client render disagree with server markup. That is the right
   * instinct and it does not apply here: this is a client-rendered Vite SPA
   * with no SSR and no hydration pass, so there is no server markup to
   * disagree with. If this app ever gains SSR, this has to move back behind an
   * effect and the flash must be solved with a blocking inline script in
   * index.html instead.
   *
   * `readStoredTheme` already swallows blocked or unavailable storage and
   * returns null, so the initialiser cannot throw in private browsing.
   */
  const [theme, setTheme] = useState<ThemeName>(
    () => readStoredTheme() ?? defaultTheme,
  );

  // The React equivalent of the canvas's <helmet>. Injected per-page rather
  // than added to index.html: the app already loads five families globally and
  // this design uses none of them, so every other page would otherwise pay for
  // two families it never renders. Guarded by id so a fast remount cannot leave
  // two copies behind.
  useEffect(() => {
    if (!document.getElementById(FONT_LINK_ID)) {
      const link = document.createElement("link");
      link.id = FONT_LINK_ID;
      link.rel = "stylesheet";
      link.href = FONT_HREF;
      document.head.appendChild(link);
    }
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement("style");
      style.id = STYLE_ID;
      style.textContent = DCV2_CSS;
      document.head.appendChild(style);
    }
    return () => {
      document.getElementById(FONT_LINK_ID)?.remove();
      document.getElementById(STYLE_ID)?.remove();
    };
  }, []);

  useDcMotion(rootRef, { parallaxIntensity });

  const dark = theme === "dark";
  const vals = themeVals(theme);

  /**
   * Keep the tab icon on the same theme as the page.
   *
   * The site already ships both masters and already swaps them — but that swap
   * lives in `components/common/ThemeToggle`, which reads the `theme` key and
   * belongs to the pre-canvas surfaces. This canvas has its own theme under
   * `ai2aim-theme` and never renders that toggle, so on every dcv2 route the
   * favicon simply stayed on whatever the last non-canvas page left behind —
   * dark chrome with a light mark, or the reverse.
   *
   * Written against the existing `link#favicon` rather than a second element,
   * so the two owners set the same node and cannot fight over which is last.
   */
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>("link#favicon");
    if (link) {
      link.href = dark
        ? "/brand/favicon-32-dark.png"
        : "/brand/favicon-32.png";
    }

    /* The scrollbar has the same problem the favicon had, for the same reason:
       it belongs to the document root, and this canvas paints its palette onto
       a div *inside* the body. Nothing on <html> can read var(--ink-rgb), so a
       styled scrollbar cannot follow the theme without the theme being mirrored
       out here. `color-scheme` comes along because it is what tells the engine
       to render form controls, and any overlay scrollbar it draws itself, in
       the matching polarity.

       Restored on unmount so a route that is not a canvas page gets the
       document back exactly as this found it. */
    const root = document.documentElement;
    const prevTheme = root.dataset.dcTheme;
    const prevScheme = root.style.colorScheme;
    root.dataset.dcTheme = dark ? "dark" : "light";
    root.style.colorScheme = dark ? "dark" : "light";
    return () => {
      if (prevTheme === undefined) delete root.dataset.dcTheme;
      else root.dataset.dcTheme = prevTheme;
      root.style.colorScheme = prevScheme;
    };
  }, [dark]);

  const toggleTheme = () => {
    const next: ThemeName = dark ? "light" : "dark";
    writeStoredTheme(next);
    setTheme(next);
  };

  return (
    <DcThemeContext.Provider value={{ theme, dark, vals, toggleTheme }}>
      <div
        id="top"
        ref={rootRef}
        className="dcv2"
        style={{
          ...paletteVars(theme),
          background: "var(--paper)",
          color: "var(--ink)",
          fontFamily: "'Manrope', sans-serif",
          overflow: "clip",
          fontSize: 16,
          lineHeight: 1.55,
          minHeight: "100vh",
          transition: "background .45s, color .45s",
        }}
      >
        {/* `dark` is passed rather than read from the context: Nav is imported
            by this file, so a `useDcTheme` call inside it would close an import
            cycle. The bar already takes its theme-derived values this way. */}
        <Nav
          dark={dark}
          themeIcon={vals.themeIcon}
          themeLabel={vals.themeLabel}
          onToggleTheme={toggleTheme}
        />
        {children}
        {!hideFooter && <Footer dark={dark} />}
      </div>
    </DcThemeContext.Provider>
  );
}
